import { useCallback, useEffect, useRef, useState } from 'react';

import { isSafari } from '../utils/navigator';

export const DEFAULT_AUDIO_VOLUME = isSafari ? 0.5 : 0.2;

const useAutoFade = ({
  delay,
  eventListenerDependency,
  callback,
}: {
  delay: number;
  eventListenerDependency: string | boolean;
  callback?: () => void;
}) => {
  const [isInvisible, setIsInvisible] = useState(false);
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const restartAutoReset = useCallback(() => {
    autoReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoReset = () => {
    setIsInvisible(false);
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      setIsInvisible(true);
      if (callback) callback();
    }, delay);
  };

  useEffect(() => {
    const onMouseMove = () => {
      restartAutoReset();
    };
    restartAutoReset();
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touch', onMouseMove); // Mobile and touch
    window.addEventListener('drag', onMouseMove); // Mobile and touch
    window.addEventListener('click', onMouseMove); // Mobile and touch
    window.addEventListener('scroll', onMouseMove); // Mobile and touch
    if (document.fullscreenElement) {
      document.fullscreenElement.addEventListener('mousemove', onMouseMove);
    }
    return () => {
      setIsInvisible(false);
      if (timeout.current) {
        clearTimeout(timeout.current);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('touch', onMouseMove); // Mobile and touch
        window.removeEventListener('drag', onMouseMove); // Mobile and touch
        window.removeEventListener('click', onMouseMove); // Mobile and touch
        window.removeEventListener('scroll', onMouseMove); // Mobile and touch
        if (document.fullscreenElement) {
          document.fullscreenElement.removeEventListener('mousemove', onMouseMove);
        }
      }
    };
  }, [eventListenerDependency, restartAutoReset]);

  return { isInvisible };
};

export default useAutoFade;
